import React, { useState, useLayoutEffect, useRef } from 'react';
import useMergedRef from '@react-hook/merged-ref';
import { mergeProps } from '@react-aria/utils';
import { FocusScope } from '@react-aria/focus';
import { DismissButton, useOverlay, OverlayContainer, } from '@react-aria/overlays';
import { useFloating, flip, shift, autoUpdate, size, offset, } from '@floating-ui/react-dom';
import { useTheme, useMutationObserver } from '../../hooks/index.js';
import { PopoverElement } from './popover.styles.js';
export const Popover = React.forwardRef(({ isOpen, onClose, children, isDismissable, shouldCloseOnBlur, dataAutomation = 'popover', placement = 'bottom-start', position = 'fixed', autoFocus, contain, restoreFocus, targetRef, ...props }, forwardedRef) => {
    const overlayRef = useRef(null);
    const [targetWidth, setTargetWidth] = useState(0);
    const { overlayProps } = useOverlay({
        isOpen,
        onClose,
        isDismissable,
        shouldCloseOnBlur,
    }, overlayRef);
    const { styles } = useTheme();
    const { x, y, refs, strategy } = useFloating({
        placement,
        strategy: position,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(parseInt(styles.popover.marginTop, 10)),
            flip(),
            size({
                apply({ availableWidth, availableHeight, elements }) {
                    Object.assign(elements.floating.style, {
                        maxWidth: `${availableWidth}px`,
                        maxHeight: `${availableHeight}px`,
                    });
                },
            }),
            shift(),
        ],
    });
    const { setReference, setFloating } = refs;
    const syncTargetWidth = (ref) => {
        const width = Math.round(ref.getBoundingClientRect().width || 0);
        setTargetWidth(width);
    };
    useLayoutEffect(() => {
        setReference(targetRef?.current || null);
        if (!targetRef?.current)
            return;
        syncTargetWidth(targetRef.current);
    }, [setReference, targetRef]);
    useMutationObserver(() => {
        if (!targetRef?.current)
            return;
        syncTargetWidth(targetRef.current);
    }, targetRef);
    const ref = useMergedRef(forwardedRef, overlayRef, setFloating);
    if (!isOpen)
        return null;
    return (React.createElement(OverlayContainer, null,
        React.createElement(FocusScope, { autoFocus: autoFocus, contain: contain, restoreFocus: restoreFocus },
            React.createElement(PopoverElement, { ...mergeProps(overlayProps, props), ref: ref, "data-automation": dataAutomation, style: {
                    position: strategy,
                    top: y ?? 0,
                    left: x ?? 0,
                    minWidth: targetWidth,
                    overflowY: 'hidden',
                } },
                React.createElement(DismissButton, { onDismiss: onClose }),
                children,
                React.createElement(DismissButton, { onDismiss: onClose })))));
});
//# sourceMappingURL=popover.component.js.map